import {useState, useEffect, useCallback} from 'react';
import {debounce} from 'lodash';
import {GetLines, GetDispatchTypes, GetDispatchMachines} from 'api/dispatch/queries';
import {useLazyQuery, useQuery} from '@apollo/client';

const LIMIT = 200;

const queryOptions = {
  variables: {
    offset: 0,
    limit: LIMIT
  },
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-first'
};

export const useIsDesktop = () => {
  const DESKTOP_WIDTH_LIMIT = 768;
  const [isDesktop, setIsDesktop] = useState(true);

  const handleResize = useCallback(() => {
    const isDesktop = window.innerWidth >= DESKTOP_WIDTH_LIMIT;
    setIsDesktop(isDesktop);
  }, [setIsDesktop]);

  useEffect(() => {
    const resizeListener = window.addEventListener('resize', debounce(handleResize, 100));
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);
  return isDesktop;
};

const fetch = (query, offset, field, setOffset, setDone) =>
  query({
    variables: {
      offset,
      limit: LIMIT
    },
    updateQuery: (previousData, {fetchMoreResult}) => {
      setOffset(offset);
      const previousResults = previousData?.[field]?.results;
      const updatedResults = fetchMoreResult?.[field]?.results;

      const isLastPage = updatedResults.length < LIMIT;
      if (isLastPage) {
        setDone(true);
        return previousData;
      }

      return {
        ...previousData,
        [field]: {
          ...previousData[field],
          results: [...previousResults, ...updatedResults]
        }
      };
    }
  });

export const useLinesQuery = () => {
  const {data, loading: linesLoading, fetchMore} = useQuery(GetLines, queryOptions);
  const [done, setDone] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [lastOffset, setLastOffset] = useState(0);
  const lines = data?.dispatchLines.results || [];

  const fetchMoreLines = () => {
    if (done || fetchingMore) {
      return;
    }

    setFetchingMore(true);
    const offset = lastOffset + LIMIT;

    fetch(fetchMore, offset, 'dispatchLines', setLastOffset, setDone).finally(() => {
      setFetchingMore(false);
    });
  };

  return {lines, linesLoading, fetchMoreLines, done};
};

export const useDispatchTypesQuery = () => {
  const {data, loading: typesLoading, fetchMore} = useQuery(GetDispatchTypes, queryOptions);
  const [done, setDone] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [lastOffset, setLastOffset] = useState(0);
  const dispatchTypes = data?.dispatchTypes.results || [];

  const fetchMoreTypes = () => {
    if (done || fetchingMore) {
      return;
    }

    setFetchingMore(true);
    const offset = lastOffset + LIMIT;

    fetch(fetchMore, offset, 'dispatchTypes', setLastOffset, setDone).finally(() => {
      setFetchingMore(false);
    });
  };

  return {dispatchTypes, typesLoading, fetchMoreTypes, done};
};

export const useDispatchMachinesQuery = () => {
  const [dispatchMachinesQuery, {data, loading: machinesLoading, fetchMore}] = useLazyQuery(
    GetDispatchMachines,
    queryOptions
  );
  const [done, setDone] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [lastOffset, setLastOffset] = useState(0);
  const dispatchMachines = data?.dispatchMachines.results || [];

  const fetchMoreMachines = () => {
    if (done || fetchingMore) {
      return;
    }

    setFetchingMore(true);
    const offset = lastOffset + LIMIT;

    fetch(fetchMore, offset, 'dispatchMachines', setLastOffset, setDone).finally(() => {
      setFetchingMore(false);
    });
  };

  return {dispatchMachinesQuery, dispatchMachines, machinesLoading, fetchMoreMachines, done};
};
